import { useState, useEffect } from 'react';
import axios from 'axios';
import { History, Filter, Trash2, Edit2, X, Check, FileDown, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Formatta ore decimali in "h X m Y"
const formatOreMinuti = (oreDecimali) => {
  if (!oreDecimali && oreDecimali !== 0) return '-';
  const ore = Math.floor(oreDecimali);
  const minuti = Math.round((oreDecimali - ore) * 60);
  return `h ${ore} m ${minuti.toString().padStart(2, '0')}`;
};

const Movimenti = () => {
  const [movimenti, setMovimenti] = useState([]);
  const [filteredMovimenti, setFilteredMovimenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterData, setFilterData] = useState('');
  const [groupByLotto, setGroupByLotto] = useState(false);
  const [sortLottoOrder, setSortLottoOrder] = useState('asc'); // 'asc' o 'desc'
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [fornitori, setFornitori] = useState([]);
  const [prodotti, setProdotti] = useState([]);

  useEffect(() => {
    fetchMovimenti();
    fetchFornitori();
    fetchProdotti();
  }, []);

  const fetchFornitori = async () => {
    try {
      const response = await axios.get(`${API}/fornitori`);
      setFornitori(response.data);
    } catch (error) {
      console.error('Error fetching fornitori');
    }
  };

  const fetchProdotti = async () => {
    try {
      const response = await axios.get(`${API}/prodotti`);
      setProdotti(response.data);
    } catch (error) {
      console.error('Error fetching prodotti');
    }
  };

  useEffect(() => {
    applyFilters();
  }, [filterTipo, searchTerm, filterData, groupByLotto, sortLottoOrder, movimenti]);

  const fetchMovimenti = async () => {
    try {
      const response = await axios.get(`${API}/movimenti`);
      
      // Ordina per: Lotto → Bin → TIMESTAMP (cronologico)
      // Questo mostra il flusso reale della merce nel tempo
      const sorted = response.data.sort((a, b) => {
        // 1. Per lotto
        if (a.lotto_number !== b.lotto_number) {
          return a.lotto_number.localeCompare(b.lotto_number);
        }
        // 2. Per bin
        if (a.bin_number !== b.bin_number) {
          return a.bin_number.localeCompare(b.bin_number);
        }
        // 3. Per timestamp (ordine cronologico)
        return new Date(a.timestamp) - new Date(b.timestamp);
      });
      
      setMovimenti(sorted);
    } catch (error) {
      toast.error('Errore nel caricamento movimenti');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (exportFormat) => {
    try {
      const params = new URLSearchParams();
      if (filterTipo !== 'all') params.append('tipo', filterTipo);
      if (searchTerm) params.append('search', searchTerm);
      if (filterData) params.append('data', filterData);
      if (groupByLotto) params.append('group_by_lotto', 'true');
      params.append('sort_order', sortLottoOrder);
      
      const apiUrl = `${API}/movimenti/export/${exportFormat}${params.toString() ? '?' + params.toString() : ''}`;
      
      if (exportFormat === 'pdf') {
        // Apri PDF in nuova finestra per garantire download
        window.open(apiUrl, '_blank');
        toast.success('Report PDF in download...');
      } else {
        // Per Excel usa blob download
        const response = await axios.get(apiUrl, { responseType: 'blob' });
        const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
        link.setAttribute('download', `report_movimenti_${date}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('Report EXCEL scaricato');
      }
    } catch (error) {
      toast.error('Errore durante esportazione');
    }
  };

  const applyFilters = () => {
    let filtered = [...movimenti];
    
    if (filterTipo !== 'all') {
      filtered = filtered.filter(m => m.tipo_movimento === filterTipo);
    }
    if (searchTerm) {
      filtered = filtered.filter(m =>
        m.bin_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.lotto_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.fornitore.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterData) {
      filtered = filtered.filter(m => {
        const movDate = format(new Date(m.timestamp), 'yyyy-MM-dd');
        return movDate === filterData;
      });
    }

    // Ordina per: Lotto → Bin → TIMESTAMP (cronologico)
    // Questo mostra il flusso reale della merce nel tempo
    filtered.sort((a, b) => {
      // 1. Per lotto
      const lottoCompare = a.lotto_number.localeCompare(b.lotto_number);
      if (lottoCompare !== 0) {
        return sortLottoOrder === 'asc' ? lottoCompare : -lottoCompare;
      }
      // 2. Per bin
      const binCompare = a.bin_number.localeCompare(b.bin_number);
      if (binCompare !== 0) {
        return binCompare;
      }
      // 3. Per timestamp (ordine cronologico)
      return new Date(a.timestamp) - new Date(b.timestamp);
    });

    setFilteredMovimenti(filtered);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredMovimenti.map(m => m.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Eliminare ${selectedIds.length} movimenti selezionati?`)) return;
    
    try {
      await axios.post(`${API}/movimenti/delete-multiple`, { ids: selectedIds });
      toast.success(`${selectedIds.length} movimenti eliminati`);
      setSelectedIds([]);
      fetchMovimenti();
    } catch (error) {
      toast.error('Errore durante eliminazione');
    }
  };

  const handleDeleteOne = async (id) => {
    if (!window.confirm('Eliminare questo movimento?')) return;
    try {
      await axios.delete(`${API}/movimenti/${id}`);
      toast.success('Movimento eliminato');
      fetchMovimenti();
    } catch (error) {
      toast.error('Errore durante eliminazione');
    }
  };

  const startEdit = (movimento) => {
    setEditingId(movimento.id);
    // Estrai data e ora dal timestamp
    let dataStr = '';
    let oraStr = '';
    if (movimento.timestamp || movimento.data_ingresso) {
      const ts = movimento.timestamp || movimento.data_ingresso;
      try {
        const d = new Date(ts);
        dataStr = d.toISOString().split('T')[0];
        oraStr = d.toTimeString().substring(0, 5);
      } catch (e) {}
    }
    
    setEditForm({
      tipo_movimento: movimento.tipo_movimento,
      bin_number: movimento.bin_number,
      doccia_number: movimento.doccia_number,
      lotto_number: movimento.lotto_number || '',
      fornitore: movimento.fornitore || '',
      prodotto: movimento.prodotto || '',
      kg_movimento: movimento.kg_movimento,
      kg_residuo: movimento.kg_residuo || 0,
      data: dataStr,
      ora: oraStr,
      note: movimento.note || ''
    });
    setShowEditModal(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
    setShowEditModal(false);
  };

  const saveEdit = async (id) => {
    try {
      // Prepara payload
      const payload = { ...editForm };
      
      // Combina data e ora in timestamp
      if (editForm.data && editForm.ora) {
        payload.timestamp = `${editForm.data}T${editForm.ora}:00`;
        payload.data_ingresso = `${editForm.data}T${editForm.ora}:00`;
      }
      delete payload.data;
      delete payload.ora;
      
      // Converti numerici
      payload.kg_movimento = parseFloat(payload.kg_movimento) || 0;
      payload.kg_residuo = parseFloat(payload.kg_residuo) || 0;
      payload.doccia_number = parseInt(payload.doccia_number) || 1;
      
      await axios.put(`${API}/movimenti/${id}`, payload);
      toast.success('Movimento aggiornato e dati sincronizzati');
      setEditingId(null);
      setShowEditModal(false);
      fetchMovimenti();
    } catch (error) {
      toast.error('Errore durante aggiornamento');
    }
  };

  const getTipoBadge = (tipo) => {
    const styles = {
      ingresso: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      uscita_parziale: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      uscita_totale: 'bg-red-500/20 text-red-400 border-red-500/30',
      spostamento: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      spostamento_bin: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      spostamento_doccia: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      trasferimento: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      trasferimento_uscita: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      trasferimento_entrata: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
      completamento: 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    const labels = {
      ingresso: 'Ingresso',
      uscita_parziale: 'Uscita Parz.',
      uscita_totale: 'Uscita Tot.',
      spostamento: 'Spostamento',
      spostamento_bin: 'Spost. Bin',
      spostamento_doccia: 'Spost. Doccia',
      trasferimento: 'Trasferim.',
      trasferimento_uscita: 'Unione ➜',
      trasferimento_entrata: '➜ Unione',
      completamento: 'Completam.'
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium border ${styles[tipo]}`}>
        {labels[tipo]}
      </span>
    );
  };

  const getLottoSeparator = (movimento, index) => {
    if (!groupByLotto) return null;
    if (index === 0 || filteredMovimenti[index - 1].lotto_number !== movimento.lotto_number) {
      return (
        <tr key={`separator-${movimento.lotto_number}-${index}`} className="bg-[#FFE500]/10">
          <td colSpan="13" className="py-2 px-4 text-[#FFE500] font-bold text-sm uppercase">
            Lotto: {movimento.lotto_number} - {movimento.fornitore}
          </td>
        </tr>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
        <div className="w-8 h-8 border-4 border-[#FFE500] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="movimenti-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#FFE500] p-2 rounded-xl">
            <History className="w-5 h-5 text-[#0A1628]" />
          </div>
          <h2 className="text-2xl font-bold text-white uppercase tracking-tight">Report Movimenti</h2>
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-xl font-bold uppercase tracking-wide text-xs hover:bg-red-600 transition-colors"
              data-testid="delete-selected-btn"
            >
              <Trash2 className="w-4 h-4" />
              Elimina ({selectedIds.length})
            </button>
          )}
          <button
            onClick={() => handleExport('pdf')}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-xl font-bold uppercase tracking-wide text-xs hover:bg-red-600 transition-colors"
            data-testid="export-movimenti-pdf-btn"
          >
            <FileDown className="w-4 h-4" />
            PDF
          </button>
          <button
            onClick={() => handleExport('excel')}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl font-bold uppercase tracking-wide text-xs hover:bg-emerald-600 transition-colors"
            data-testid="export-movimenti-excel-btn"
          >
            <FileDown className="w-4 h-4" />
            EXCEL
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-5" data-testid="filters-section">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-[#FFE500]" />
          <h3 className="text-sm font-bold text-white uppercase tracking-tight">Filtri</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Data</label>
            <input
              type="date"
              value={filterData}
              onChange={(e) => setFilterData(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
              data-testid="data-filter"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Tipo Movimento</label>
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
              data-testid="tipo-filter"
            >
              <option value="all" className="bg-[#0A1628]">Tutti</option>
              <option value="ingresso" className="bg-[#0A1628]">Ingressi</option>
              <option value="uscita_parziale" className="bg-[#0A1628]">Uscite Parziali</option>
              <option value="uscita_totale" className="bg-[#0A1628]">Uscite Totali</option>
              <option value="spostamento_bin" className="bg-[#0A1628]">Spostamenti Bin</option>
              <option value="spostamento_doccia" className="bg-[#0A1628]">Spostamenti Doccia</option>
              <option value="trasferimento_uscita" className="bg-[#0A1628]">Unioni (Uscita)</option>
              <option value="trasferimento_entrata" className="bg-[#0A1628]">Unioni (Entrata)</option>
              <option value="completamento" className="bg-[#0A1628]">Completamenti</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1 uppercase">Ricerca</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca bin, lotto o fornitore..."
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer bg-white/10 px-3 py-2 rounded-xl border border-white/20">
              <input
                type="checkbox"
                checked={groupByLotto}
                onChange={(e) => setGroupByLotto(e.target.checked)}
                className="w-5 h-5 rounded border-white/20 text-[#FFE500] focus:ring-[#FFE500] bg-white/10"
                data-testid="group-by-lotto"
              />
              <span className="text-sm font-medium text-white">Raggruppa per Lotto</span>
            </label>
          </div>
          {/* Ordinamento Lotto */}
          <div className="flex items-end">
            <button
              onClick={() => setSortLottoOrder(sortLottoOrder === 'asc' ? 'desc' : 'asc')}
              className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-xl border border-white/20 hover:bg-white/20 transition-colors"
              data-testid="sort-lotto-btn"
            >
              {sortLottoOrder === 'asc' ? (
                <ArrowUp className="w-4 h-4 text-[#FFE500]" />
              ) : (
                <ArrowDown className="w-4 h-4 text-[#FFE500]" />
              )}
              <span className="text-sm font-medium text-white">
                Lotto {sortLottoOrder === 'asc' ? 'A→Z' : 'Z→A'}
              </span>
            </button>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-white/50">
            Mostrando <span className="text-white font-medium">{filteredMovimenti.length}</span> di <span className="text-white font-medium">{movimenti.length}</span> movimenti
          </span>
          {(filterData || filterTipo !== 'all' || searchTerm || groupByLotto) && (
            <button
              onClick={() => { setFilterData(''); setFilterTipo('all'); setSearchTerm(''); setGroupByLotto(false); }}
              className="text-sm text-red-400 hover:text-red-300 transition-colors"
              data-testid="clear-filters-btn"
            >
              Pulisci filtri
            </button>
          )}
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl overflow-hidden" data-testid="movimenti-table-container">
        <div className="overflow-x-auto">
          <table className="text-sm w-full border-collapse" data-testid="movimenti-table">
            <thead>
              <tr className="bg-[#FFE500]/10">
                <th className="py-3 px-2 text-left w-10">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedIds.length === filteredMovimenti.length && filteredMovimenti.length > 0}
                    className="w-4 h-4 rounded bg-white/10 border-white/20"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-2 text-left">Data Ingresso</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-2 text-left">Tipo</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-2 text-left">Bin</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-2 text-left">Doccia</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-2 text-left">Lotto</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-2 text-left">Fornitore</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-2 text-left">KG</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-2 text-left">Residuo</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-2 text-left">Data Uscita</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-2 text-left">Ore Dep.</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-2 text-left">Note</th>
                <th className="text-[#FFE500] uppercase text-xs font-bold py-3 px-2 text-center w-20">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredMovimenti.length === 0 ? (
                <tr>
                  <td colSpan="13" className="text-center py-8 text-white/50" data-testid="no-movimenti-message">
                    {movimenti.length === 0 ? 'Nessun movimento registrato.' : 'Nessun movimento trovato con i filtri selezionati.'}
                  </td>
                </tr>
              ) : (
                filteredMovimenti.map((movimento, index) => (
                  <>
                    {getLottoSeparator(movimento, index)}
                    <tr key={movimento.id} className="border-b border-white/10 hover:bg-white/5 transition-colors" data-testid={`movimento-row-${index}`}>
                      <td className="py-2 px-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(movimento.id)}
                          onChange={() => handleSelectOne(movimento.id)}
                          className="w-4 h-4 rounded bg-white/10 border-white/20"
                        />
                      </td>
                      <td className="py-2 px-2 text-xs text-white/70">
                        {movimento.data_ingresso ? format(new Date(movimento.data_ingresso), 'dd/MM/yy HH:mm') : '-'}
                      </td>
                      <td className="py-2 px-2">{getTipoBadge(movimento.tipo_movimento)}</td>
                      <td className="py-2 px-2 font-medium text-xs text-white">
                        {movimento.bin_number}
                        {movimento.bin_destinazione && <span className="text-white/40"> - {movimento.bin_destinazione}</span>}
                      </td>
                      <td className="py-2 px-2 text-xs text-white/70">
                        {movimento.doccia_number}
                        {movimento.doccia_destinazione && <span className="text-white/40"> - {movimento.doccia_destinazione}</span>}
                      </td>
                      <td className="py-2 px-2 text-xs text-white/70">{movimento.lotto_number}</td>
                      <td className="py-2 px-2 text-xs text-white/70">{movimento.fornitore}</td>
                      <td className="py-2 px-2 font-bold text-xs text-white">
                        {editingId === movimento.id ? (
                          <input
                            type="number"
                            value={editForm.kg_movimento}
                            onChange={(e) => setEditForm({...editForm, kg_movimento: parseFloat(e.target.value)})}
                            className="w-16 px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white"
                          />
                        ) : movimento.kg_movimento}
                      </td>
                      <td className="py-2 px-2 font-bold text-red-400 text-xs">
                        {editingId === movimento.id ? (
                          <input
                            type="number"
                            value={editForm.kg_residuo}
                            onChange={(e) => setEditForm({...editForm, kg_residuo: parseFloat(e.target.value)})}
                            className="w-16 px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white"
                          />
                        ) : movimento.kg_residuo}
                      </td>
                      <td className="py-2 px-2 text-xs text-white/70">
                        {movimento.data_ora_uscita ? format(new Date(movimento.data_ora_uscita), 'dd/MM/yy HH:mm') : '-'}
                      </td>
                      <td className="py-2 px-2 text-xs font-bold text-purple-400">
                        {movimento.ore_depurazione ? formatOreMinuti(movimento.ore_depurazione) : '-'}
                      </td>
                      <td className="py-2 px-2 text-xs text-white/50 max-w-[100px] truncate">
                        {editingId === movimento.id ? (
                          <input
                            type="text"
                            value={editForm.note}
                            onChange={(e) => setEditForm({...editForm, note: e.target.value})}
                            className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white"
                          />
                        ) : movimento.note}
                      </td>
                      <td className="py-2 px-2 text-center">
                        {editingId === movimento.id ? (
                          <div className="flex justify-center gap-1">
                            <button onClick={() => saveEdit(movimento.id)} className="p-1 text-emerald-400 hover:bg-emerald-500/20 rounded">
                              <Check className="w-4 h-4" />
                            </button>
                            <button onClick={cancelEdit} className="p-1 text-white/50 hover:bg-white/10 rounded">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-center gap-1">
                            <button onClick={() => startEdit(movimento)} className="p-1 text-blue-400 hover:bg-blue-500/20 rounded" data-testid={`edit-btn-${index}`}>
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeleteOne(movimento.id)} className="p-1 text-red-400 hover:bg-red-500/20 rounded" data-testid={`delete-btn-${index}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  </>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Modifica Movimento */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="edit-movimento-modal">
          <div className="bg-[#0A1628] border border-white/20 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Modifica Movimento</h3>
                <button onClick={cancelEdit} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-white/70" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Tipo Movimento */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Tipo Movimento</label>
                <select
                  value={editForm.tipo_movimento}
                  onChange={(e) => setEditForm({...editForm, tipo_movimento: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  data-testid="edit-tipo-select"
                >
                  <option value="ingresso" className="bg-[#0A1628]">Ingresso</option>
                  <option value="uscita_parziale" className="bg-[#0A1628]">Uscita Parziale</option>
                  <option value="uscita_totale" className="bg-[#0A1628]">Uscita Totale</option>
                  <option value="spostamento_bin" className="bg-[#0A1628]">Spostamento Bin</option>
                  <option value="spostamento_doccia" className="bg-[#0A1628]">Spostamento Doccia</option>
                  <option value="completamento" className="bg-[#0A1628]">Completamento</option>
                </select>
              </div>

              {/* Bin e Doccia */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Bin Number</label>
                  <input
                    type="text"
                    value={editForm.bin_number}
                    onChange={(e) => setEditForm({...editForm, bin_number: e.target.value})}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                    data-testid="edit-bin-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Doccia</label>
                  <select
                    value={editForm.doccia_number}
                    onChange={(e) => setEditForm({...editForm, doccia_number: e.target.value})}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                    data-testid="edit-doccia-select"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(num => (
                      <option key={num} value={num} className="bg-[#0A1628]">Doccia {num}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Lotto */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Lotto</label>
                <input
                  type="text"
                  value={editForm.lotto_number}
                  onChange={(e) => setEditForm({...editForm, lotto_number: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  data-testid="edit-lotto-input"
                />
              </div>

              {/* Fornitore */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Fornitore</label>
                <select
                  value={editForm.fornitore}
                  onChange={(e) => setEditForm({...editForm, fornitore: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  data-testid="edit-fornitore-select"
                >
                  <option value="" className="bg-[#0A1628]">Seleziona fornitore...</option>
                  {fornitori.map(f => (
                    <option key={f.nome} value={f.nome} className="bg-[#0A1628]">{f.nome}</option>
                  ))}
                </select>
              </div>

              {/* Prodotto */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Prodotto</label>
                <select
                  value={editForm.prodotto}
                  onChange={(e) => setEditForm({...editForm, prodotto: e.target.value})}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                  data-testid="edit-prodotto-select"
                >
                  <option value="" className="bg-[#0A1628]">Seleziona prodotto...</option>
                  {prodotti.map(p => (
                    <option key={p.nome} value={p.nome} className="bg-[#0A1628]">{p.nome} {p.sigla && `[${p.sigla}]`}</option>
                  ))}
                </select>
              </div>

              {/* KG Movimento e Residuo */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">KG Movimento</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.kg_movimento}
                    onChange={(e) => setEditForm({...editForm, kg_movimento: e.target.value})}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                    data-testid="edit-kg-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">KG Residuo</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.kg_residuo}
                    onChange={(e) => setEditForm({...editForm, kg_residuo: e.target.value})}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                    data-testid="edit-kg-residuo-input"
                  />
                </div>
              </div>

              {/* Data e Ora */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Data</label>
                  <input
                    type="date"
                    value={editForm.data}
                    onChange={(e) => setEditForm({...editForm, data: e.target.value})}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                    data-testid="edit-data-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Ora</label>
                  <input
                    type="time"
                    value={editForm.ora}
                    onChange={(e) => setEditForm({...editForm, ora: e.target.value})}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500]"
                    data-testid="edit-ora-input"
                  />
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Note</label>
                <textarea
                  value={editForm.note}
                  onChange={(e) => setEditForm({...editForm, note: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE500] resize-none"
                  data-testid="edit-note-textarea"
                />
              </div>
            </div>

            <div className="p-6 border-t border-white/10 flex gap-3 justify-end">
              <button
                onClick={cancelEdit}
                className="px-6 py-2 bg-white/10 text-white border border-white/20 rounded-xl hover:bg-white/20 transition-colors"
                data-testid="edit-cancel-btn"
              >
                Annulla
              </button>
              <button
                onClick={() => saveEdit(editingId)}
                className="px-6 py-2 bg-[#FFE500] text-[#0A1628] rounded-xl font-bold hover:bg-[#FFE500]/90 transition-colors"
                data-testid="edit-save-btn"
              >
                Salva Modifiche
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Movimenti;
